import { useMemo, useState } from 'react'
import type { TimelineBoardKey } from '../../types/schema'
import { useTimeline } from '../../context/useTimeline'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import { Select } from '../Common'
import { AppDialog } from '../Common/AppDialog'
import { Pressable } from '../Common/Pressable'
import { getManageBoardLabel, getManageParentKindLabels } from '../Manage/ManageNls'

interface BinMoveDialogProps {
  elementId: string
  onClose: () => void
}

const TARGET_BOARDS: TimelineBoardKey[] = ['VISIONBOARD', 'DASHBOARD', 'ARCHIVE']

export const BinMoveDialog = ({ elementId, onClose }: BinMoveDialogProps) => {
  const { elements, moveFromBin } = useTimeline()
  const { showToast } = useToast()
  const { t } = useNls()
  const [targetBoard, setTargetBoard] = useState<TimelineBoardKey>('DASHBOARD')
  const [parentId, setParentId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const element = useMemo(
    () => elements.find((entry) => entry.id === elementId) ?? null,
    [elements, elementId],
  )

  const requiredParentType: 'GOAL' | 'TASK' | null = useMemo(() => {
    if (!element) return null
    if (element.type === 'TASK') return 'GOAL'
    if (element.type === 'SUBTASK') return 'TASK'
    return null
  }, [element])

  const parentCandidates = useMemo(() => {
    if (!requiredParentType) return []
    return elements
      .filter((entry) => entry.type === requiredParentType && entry.board === targetBoard)
      .map((entry) => ({ id: entry.id, title: entry.title || t('NLSManageItemTitleFallback') }))
  }, [elements, requiredParentType, targetBoard, t])

  const handleTargetBoardChange = (next: TimelineBoardKey) => {
    setTargetBoard(next)
    setParentId(null)
  }

  const parentRequired = requiredParentType !== null
  const canConfirm =
    !!element &&
    !busy &&
    (!parentRequired || (parentId !== null && parentCandidates.some((entry) => entry.id === parentId)))

  const handleConfirm = async () => {
    if (!element || busy) return
    if (parentRequired && !parentId) {
      showToast(t('NLSManageBinMovePickParentToast'), 'warning')
      return
    }
    setBusy(true)
    try {
      await moveFromBin(elementId, targetBoard, parentRequired ? parentId : null)
      showToast(t('NLSManageStatusMovedOutOfBin', { title: element.title || t('NLSManageItemFallback') }), 'success')
      onClose()
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : t('NLSManageStatusMoveFailed')
      showToast(message, 'warning')
      setBusy(false)
    }
  }

  if (!element) {
    return (
      <AppDialog
        open={true}
        size="sm"
        title={t('NLSManageTitleBinItemNotFound')}
        message={t('NLSManageTitleItemUnavailable')}
        onDismiss={onClose}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'ghost',
            onClick: onClose,
          },
        ]}
      />
    )
  }

  return (
    <AppDialog
      open={true}
      size="sm"
      title={t('NLSManageBinMoveTitle')}
      message={t('NLSManageBinMoveBody', {
        title: element.title || t('NLSManageItemTitleFallback'),
        type: element.type.toLowerCase(),
      })}
      onDismiss={busy ? undefined : onClose}
      actions={[
        {
          label: t('NLSCommonActionCancel'),
          variant: 'ghost',
          disabled: busy,
          onClick: onClose,
        },
        {
          label: busy ? t('NLSManageActionMoving') : t('NLSManageActionMove'),
          variant: 'primary',
          disabled: !canConfirm,
          loading: busy,
          onClick: () => void handleConfirm(),
        },
      ]}
    >
      <div className="detailedBinMoveField">
        <label className="detailedBinMoveLabel">{t('NLSManageFieldTargetBoard')}</label>
        <div className="detailedBinMoveBoardRow" role="radiogroup" aria-label={t('NLSManageFieldTargetBoard')}>
          {TARGET_BOARDS.map((board) => {
            const isActive = targetBoard === board
            return (
              <Pressable
                key={board}
                role="radio"
                aria-checked={isActive}
                className={`detailedBinMoveBoardOption${isActive ? ' isActive' : ''}`}
                onClick={() => handleTargetBoardChange(board)}
                disabled={busy}
              >
                {getManageBoardLabel(t, board)}
              </Pressable>
            )
          })}
        </div>
      </div>

      {parentRequired && requiredParentType ? (
        <div className="detailedBinMoveField">
          <label className="detailedBinMoveLabel" htmlFor="bin-move-parent">
            {t('NLSManageBinMoveParentLabel', { type: getManageParentKindLabels(t, requiredParentType).singular })}
          </label>
          {parentCandidates.length === 0 ? (
            <p className="detailedBinMoveEmpty">
              {t('NLSManageBinMoveEmpty', { typePlural: getManageParentKindLabels(t, requiredParentType).plural })}
            </p>
          ) : (
            <Select
              trigger="pill"
              ariaLabel={t('NLSManageBinMoveSelectAria', { type: getManageParentKindLabels(t, requiredParentType).singular })}
              className="detailedBinMoveSelect"
              value={parentId ?? ''}
              disabled={busy}
              options={[
                {
                  value: '',
                  label: t('NLSManageBinMoveSelectPlaceholder', { type: getManageParentKindLabels(t, requiredParentType).singular }),
                  disabled: true,
                },
                ...parentCandidates.map((entry) => ({ value: entry.id, label: entry.title })),
              ]}
              onChange={(value) => setParentId(value || null)}
            />
          )}
        </div>
      ) : (
        <p className="detailedBinMoveHint">
          {t('NLSManageBinMoveHintGoalsRoot')}
        </p>
      )}
    </AppDialog>
  )
}
